import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import UsersService from "@velocity-ehs/user-utils-module";
import {
  getPagination,
  PaginationQueryObject,
  QuerySort,
} from "../components/supportFunctions/graphql/Queries";
import { useQuery } from "@tanstack/react-query";
import GatewayService from "../services/GatewayService";
import {
  ExplicitAssigned,
  ExplicitAssignedOptions,
  GetUsersAssignedToRoleFilter,
  UserAssignedToRole,
} from "../components/routeComponents/RoleManagement/Utils";
import { PageInfo } from "../components/supportFunctions/TableReducer";
import { OptionItem } from "../components/types";

function useGetUsersAssignedToRole(
  roleID: string,
  params?: any,
  location?: {
    id: string;
  }
) {
  const { showSnackbar } = useSnackbarActionContext();
  const { pagination, filters } = formatParams(params, roleID, location);

  const result = useQuery({
    queryKey: ["usersAssignedToRole", location, roleID, params],
    queryFn: () => GatewayService.getUsersAssignedToRole(filters, pagination),
    retry: false,
    enabled: !!location && !!roleID,
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
  });

  return {
    ...result,
    usersAssignedToRole: (result.data?.items ||
      []) as Array<UserAssignedToRole>,
    pagination: (result.data?.pageInfo || {}) as PageInfo,
  };
}

function formatParams(
  params: any,
  roleID: string,
  rootLocation?: { id: string }
) {
  let querySort: QuerySort = null;

  if (params.sortField && params.sortOrder) {
    querySort = {
      sortField: params.sortField,
      sortDirection: params.sortOrder,
    } as QuerySort;
  }

  let pagination: PaginationQueryObject = getPagination(
    params.page + 1,
    params.rows,
    querySort
  );

  const filters: GetUsersAssignedToRoleFilter = {
    and: {
      customerID: UsersService.customer.id,
      locationID: rootLocation?.id,
      roleID,
      displayName: params.filters?.displayName?.value || undefined,
      userStatus: params.filters?.userStatus?.value || undefined,
      isExplicitlyAssigned: getExplicitlyAssignedFilterValue(
        params.filters?.isExplicitlyAssigned?.value
      ),
    },
  };

  return { filters, pagination };
}

function getExplicitlyAssignedFilterValue(
  selection: OptionItem[]
): boolean | undefined {
  if (selection.length === 1) {
    return selection[0].id === ExplicitAssigned.Yes;
  }
  return undefined;
}

export { useGetUsersAssignedToRole };
